/**
 * Console access to the consent layer, for checking and experimenting with what consumers actually
 * see: `window.playConsent` in the browser console.
 *
 * The point is to answer "what is the consent layer telling consumers right now, and why" without
 * digging through cookies, local storage and network requests — and to be able to try the other
 * answer without clicking through a banner.
 *
 * ## What is exposed where
 *
 * The **readers** are always available, including in production. They only reveal a visitor's own
 * consent to their own console, which is not a secret, and being able to ask a real session what it
 * thinks is worth a great deal when something looks wrong.
 *
 * The **mutators** are left out of production entirely. They can grant full consent, which
 * releases the advid and, later, the PPID — so in a production bundle they would be a consent
 * bypass reachable by any third-party script on the page, an ad script included. Outside production
 * that risk does not exist and the ability to flip an answer and watch the next request is exactly
 * what makes this useful.
 *
 * Overrides live for the session only. They do not touch Cookiebot's own record, and any full page
 * load re-runs the real bootstrap and drops them — so navigate within the app to watch one take
 * effect on a request.
 */
import {
  ADVID_HEADER,
  ADVID_STORAGE_KEY,
  clearAdvid,
  getAdvid,
  getAdvidHeaders,
} from '@tv2/lib-consent/advid';
import {
  type ConsentCategory,
  type ConsentDecision,
  ALL_CONSENT_DENIED,
  ALL_CONSENT_GRANTED,
  CONSENT_CATEGORIES,
  areAdvertisingIdentifiersAllowed,
  getConsentDetails,
  getConsentTrackingStatus,
  getCurrentConsentDecision,
  getTcfConsentString,
  isConsentDecisionResolved,
  isConsentIntegrationEnabled,
  isPersonalizedAdsAllowed,
  setConsentDecision,
} from '@tv2/lib-consent/consentManager';
import { isConsentCategoryAllowed } from '@tv2/lib-consent/consentPolicy';
import { CONSENT_STATUS_STORAGE_KEY, getPersistedConsentStatus } from './consentStatus';

/** Every category on the manager, the IAB groups Cookiebot cannot grant included. */
const CATEGORIES: ReadonlyArray<ConsentCategory> = CONSENT_CATEGORIES;

const HELP = `window.playConsent

  state()              everything below in one object
  categories()         every category as consumers read them (IAB groups are always
                       declined on the web - Cookiebot does not expose them)
  advid()              the advid and the header
  consentStatus()      the one-word status and the per-group details on every Adobe
                       event, and the stored copy of the status
  cookiebot()          what Cookiebot itself says, to compare against
  tcf()                the raw IAB TCF consent string
  personalizedAds()    the allow_personalized_ads rollout flag (always true on the
                       web for now - only Smart TV fetches the /consent endpoint)

  decide({ Marketing: false })   override the recorded decision (session only)
  grantAll() / denyAll()         shorthands for the above
  forgetAdvid()                  drop the persisted advid, so a new one is minted
  reset()                        reload, putting Cookiebot's real answer back

Overrides do not change Cookiebot's own record, and any full page load drops
them. Navigate within the app to see one take effect on a real request.`;

type CookiebotWindow = Window & {
  Cookiebot?: { hasResponse?: boolean; consent?: Record<string, unknown> };
};

function readPersistedAdvid(): string | null {
  try {
    return localStorage.getItem(ADVID_STORAGE_KEY);
  } catch {
    return null;
  }
}

function categories(): Record<ConsentCategory, boolean> {
  const result = {} as Record<ConsentCategory, boolean>;
  for (const category of CATEGORIES) {
    result[category] = isConsentCategoryAllowed(category);
  }
  return result;
}

function advid() {
  return {
    /** What consumers get. `undefined` means it is being withheld. */
    value: getAdvid(),
    /** The stored id. Deleted, not just withheld, as soon as consent is anything but full. */
    persisted: readPersistedAdvid(),
    /** The full-consent verdict the Cookiebot provider has recorded on the manager. */
    released: areAdvertisingIdentifiersAllowed(),
    /** What the header would be on the next graph query, or `undefined` when omitted. */
    header: getAdvidHeaders()[ADVID_HEADER],
  };
}

function consentStatus() {
  return {
    /** What the next Adobe event carries as `consentStatus`. Always a value. */
    tracking: getConsentTrackingStatus(),
    /** What the next Adobe event carries as `consentDetails`; `undefined` means omitted (no CMP). */
    details: getConsentDetails(),
    /** The copy in localStorage, for anything on the page that reads it there. */
    persisted: getPersistedConsentStatus(),
    storageKey: CONSENT_STATUS_STORAGE_KEY,
  };
}

function cookiebot() {
  const cmp = (window as CookiebotWindow).Cookiebot;
  if (!cmp) return { loaded: false as const };
  return { loaded: true as const, hasResponse: cmp.hasResponse === true, consent: cmp.consent };
}

function state() {
  return {
    integrationEnabled: isConsentIntegrationEnabled(),
    decisionRecorded: isConsentDecisionResolved(),
    decision: getCurrentConsentDecision(),
    categories: categories(),
    advid: advid(),
    consentStatus: consentStatus(),
    tcfConsentString: getTcfConsentString(),
    /** The remote rollout flag, not a consent category - see isPersonalizedAdsAllowed(). */
    personalizedAdsAllowed: isPersonalizedAdsAllowed(),
    cookiebot: cookiebot(),
  };
}

function decide(partial: Partial<ConsentDecision>) {
  const base = getCurrentConsentDecision() ?? ALL_CONSENT_DENIED;
  setConsentDecision({ ...base, ...partial });
  warnOverride();
  return categories();
}

function warnOverride() {
  // biome-ignore lint/suspicious/noConsole: this tool is the console
  console.warn(
    "[playConsent] session override in effect — Cookiebot's own record is unchanged. Call playConsent.reset() to go back."
  );
}

const readers = {
  help() {
    // biome-ignore lint/suspicious/noConsole: this tool is the console
    console.log(HELP);
    return undefined;
  },
  state,
  categories,
  advid,
  consentStatus,
  cookiebot,
  tcf: getTcfConsentString,
  personalizedAds: isPersonalizedAdsAllowed,
};

const mutators = {
  decide,
  grantAll: () => decide(ALL_CONSENT_GRANTED),
  denyAll: () => decide(ALL_CONSENT_DENIED),
  forgetAdvid() {
    clearAdvid();
    // Only a full consent mints the next one; otherwise there is nothing to mint.
    return { persisted: readPersistedAdvid(), next: getAdvid() };
  },
  reset() {
    window.location.reload();
  },
};

export type ConsentDebugApi = typeof readers & Partial<typeof mutators>;

/**
 * Installs `window.playConsent`. Safe to call more than once; a second call replaces the handle
 * rather than stacking.
 *
 * Pass `includeMutators` to decide whether the write half comes along — production must not get it,
 * see the module docs.
 */
export function installConsentDebug(includeMutators: boolean): void {
  if (typeof window === 'undefined') return;

  const api: ConsentDebugApi = includeMutators ? { ...readers, ...mutators } : { ...readers };
  Object.defineProperty(window, 'playConsent', {
    value: api,
    configurable: true,
    enumerable: false,
    writable: true,
  });
}

/** The categories, for callers that want to iterate them the same way this module does. */
export { CATEGORIES as CONSENT_DEBUG_CATEGORIES };
